import { motion } from "framer-motion";
import angelaSeated from "@/assets/angela-seated.jpg";
import wtaLogo from "@/assets/wta-logo.png";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.15, duration: 0.7, ease: "easeOut" as const },
  }),
};

const PASSection = () => {
  return (
    <section className="py-24 md:py-32 relative">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_hsl(43_72%_55%_/_0.04),_transparent_60%)]" />

      <div className="container mx-auto px-6 md:px-12">
        {/* Problem */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="max-w-3xl mx-auto text-center mb-20 space-y-6"
        >
          <motion.p variants={fadeUp} custom={0} className="text-xs uppercase tracking-[0.4em] text-gold font-body font-medium">
            The Problem
          </motion.p>
          <motion.h2 variants={fadeUp} custom={1} className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-foreground leading-tight">
            You're earning well —<br />
            <span className="text-gradient-gold">but is your wealth working for you?</span>
          </motion.h2>
          <motion.p variants={fadeUp} custom={2} className="text-muted-foreground font-body text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
            You've built income, but without a stewardship strategy, your wealth stays in one generation. 
            70% of family wealth is lost by the second generation. You didn't work this hard for your legacy to end with you.
          </motion.p>
        </motion.div>

        {/* Agitation + Image */}
        <div className="grid md:grid-cols-2 gap-16 items-center mb-20">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="absolute -inset-3 border border-gold/15" />
            <img
              src={angelaSeated}
              alt="Angela Roberson teaching wealth stewardship"
              className="w-full object-cover shadow-luxury max-h-[50vh] sm:max-h-[60vh] md:max-h-none"
            />
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="space-y-6"
          >
            <motion.p variants={fadeUp} custom={0} className="text-xs uppercase tracking-[0.4em] text-gold font-body font-medium">
              The Reality
            </motion.p>
            <motion.h3 variants={fadeUp} custom={1} className="text-2xl md:text-3xl font-display font-bold text-foreground leading-tight">
              You need implementation.<br />
              <span className="text-gradient-gold">It's time to steward well.</span>
            </motion.h3>
            <motion.p variants={fadeUp} custom={2} className="text-muted-foreground font-body leading-relaxed">
              Free content and scattered advice keep you busy but not building. You're left second-guessing 
              every financial decision, unsure if you're truly protecting what you've earned or setting up 
              the next generation for success.
            </motion.p>
            <motion.p variants={fadeUp} custom={3} className="text-muted-foreground font-body leading-relaxed">
              The Wealth Transfer Academy is not just about acquiring knowledge — it's about 
              implementation and results. Without a clear framework, your wealth is vulnerable — to taxes, 
              poor planning, and missed opportunities that compound over time.
            </motion.p>
          </motion.div>
        </div>

        {/* Solution */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="max-w-3xl mx-auto text-center space-y-6"
        >
          <motion.img
            src={wtaLogo}
            alt="Wealth Transfer Academy Logo"
            variants={fadeUp}
            custom={0}
            className="w-32 h-32 md:w-40 md:h-40 object-contain mx-auto drop-shadow-[0_0_30px_hsl(43_72%_55%_/_0.3)]"
          />
          <motion.h2 variants={fadeUp} custom={1} className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-foreground leading-tight">
            Introducing the<br />
            <span className="text-gradient-gold">Wealth Transfer Academy</span>
          </motion.h2>
          <motion.p variants={fadeUp} custom={2} className="text-muted-foreground font-body text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
            Led by renowned entrepreneur and wealth coach, Angela Joseph Roberson, the Academy offers 
            comprehensive courses and training programs designed to unlock your financial potential 
            and create a legacy of abundance.
          </motion.p>
          <motion.p variants={fadeUp} custom={3} className="text-muted-foreground font-body text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
            With a proven track record of success, Angela is dedicated to sharing her expertise 
            and empowering you to achieve financial freedom.
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
};

export default PASSection;
